import { useCallback, useEffect, useState } from 'react';

import { ICartEntry } from '@rbi-ctg/menu';
import { IAddOnSection, IAddOnSectionOption } from 'hooks/use-cart-add-ons';
import useEffectOnce from 'hooks/use-effect-once';
import { useAddOnsItemContainer } from 'pages/cart/addons-item-container/hook/use-addons-item-container';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

interface IUseAddonsFreeModalProps {
  cartEntries: ICartEntry[];
  addOnSections: IAddOnSection[];
}

interface IUseAddonsFreeModalResult {
  modalOptions: IAddOnSectionOption[];
  setModalOptions: React.Dispatch<React.SetStateAction<IAddOnSectionOption[]>>;
  hasModalWithOffer: boolean;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  hasOneItem: boolean;
}

export const useAddonsFreeModal = ({
  cartEntries,
  addOnSections,
}: IUseAddonsFreeModalProps): IUseAddonsFreeModalResult => {
  const { isServiceValid, isItemsAcceptingFreeOffer, addOnHasOffer } = useAddOnsItemContainer();
  const [modalOptions, setModalOptions] = useState<IAddOnSectionOption[]>([]);
  const [showModal, setShowModal] = useState(false);
  const promotionalItemsInCart =
    LocalStorage.getItem(StorageKeys.FREE_MODAL_ITEMS_SHOWED_IN_CART) || [];

  const hasModalWithOffer = !!modalOptions.length;

  const findModalOffersToShow = useCallback((): void => {
    const optionsWithModalToggleEnabled = addOnSections
      .filter(section => {
        const { showSectionItemsOnCart, enableAddonAsFreeItem, sectionItemsServiceModes } = section;

        const isRequiredToggleEnabled = showSectionItemsOnCart && enableAddonAsFreeItem;

        return (
          isRequiredToggleEnabled &&
          isServiceValid(sectionItemsServiceModes) &&
          isItemsAcceptingFreeOffer(section, cartEntries)
        );
      })
      .flatMap(({ options }) => {
        const optionsWithFreeAddOnModalToggleEnabled = options.filter(
          ({ addOnSectionFreeModalInfos, itemsAcceptingFreeOffers }) =>
            addOnSectionFreeModalInfos?.showFreeAddOnModal &&
            addOnHasOffer(cartEntries, itemsAcceptingFreeOffers)
        );

        if (!promotionalItemsInCart?.length) {
          return optionsWithFreeAddOnModalToggleEnabled;
        }

        const optionsThatWasNotShowed = optionsWithFreeAddOnModalToggleEnabled.filter(
          ({ _key }) => {
            const isOptionShownInModal = promotionalItemsInCart?.some(
              (sectionKey: string) => sectionKey === _key
            );
            return !isOptionShownInModal;
          }
        );

        return optionsThatWasNotShowed;
      });

    setModalOptions(optionsWithModalToggleEnabled);
  }, [
    addOnHasOffer,
    addOnSections,
    cartEntries,
    isItemsAcceptingFreeOffer,
    isServiceValid,
    promotionalItemsInCart,
  ]);

  const hasOneItem = modalOptions.length === 1;

  useEffectOnce(() => {
    findModalOffersToShow();
  });

  useEffect(() => {
    if (hasModalWithOffer && !showModal) {
      modalOptions.forEach(option => {
        promotionalItemsInCart.push(option._key);
      });

      LocalStorage.setItem(StorageKeys.FREE_MODAL_ITEMS_SHOWED_IN_CART, promotionalItemsInCart);
      setShowModal(true);
    }
  }, [hasModalWithOffer, modalOptions, promotionalItemsInCart, showModal]);

  return {
    modalOptions,
    setModalOptions,
    hasModalWithOffer,
    showModal,
    setShowModal,
    hasOneItem,
  };
};
